import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export interface ExpenseCategoryRecord {
  id: string;
  name: string;
  number: string;
  accountDetailTypeId: string;
  accountDetailTypeCode: string;
  accountDetailTypeTitle: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseCategoryDraft {
  name: string;
  accountDetailTypeId: string;
}

export interface ExpenseDetailTypeOption {
  id: string;
  code: string;
  title: string;
}

const LS_KEY = "inventory.expenseCategories.v1";

type ExpenseCategoryApiRecord = {
  id?: number | string;
  name: string;
  number?: string | null;
  account_detail_type_id?: number | string | null;
  account_detail_type_code?: string | null;
  account_detail_type_title?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type UpsertExpenseCategoryApiResponse = {
  message?: string;
  data?: ExpenseCategoryApiRecord;
};

type DeleteExpenseCategoryApiResponse = {
  message?: string;
};

type UpsertExpenseCategoryResult = {
  record: ExpenseCategoryRecord;
  message: string;
};

type DeleteExpenseCategoryResult = {
  success: boolean;
  message: string;
};

function dedupeById(items: ExpenseCategoryRecord[]): ExpenseCategoryRecord[] {
  const byId = new Map<ID, ExpenseCategoryRecord>();
  for (const item of items) byId.set(item.id, item);
  return Array.from(byId.values());
}

function load(): ExpenseCategoryRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ExpenseCategoryRecord[];
    return Array.isArray(parsed)
      ? dedupeById(
          parsed.map((item) => ({
            ...item,
            number: item.number ?? "",
            accountDetailTypeId: item.accountDetailTypeId ?? "",
            accountDetailTypeCode: item.accountDetailTypeCode ?? "",
            accountDetailTypeTitle: item.accountDetailTypeTitle ?? "",
          })),
        )
      : [];
  } catch {
    return [];
  }
}

function save(items: ExpenseCategoryRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

function extractRowsFromResponse(body: unknown): ExpenseCategoryApiRecord[] {
  if (Array.isArray(body)) return body as ExpenseCategoryApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (Array.isArray(data)) return data as ExpenseCategoryApiRecord[];
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const inner = (data as Record<string, unknown>).data;
    if (Array.isArray(inner)) return inner as ExpenseCategoryApiRecord[];
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractCategoryFromUpsertBody(body: unknown): ExpenseCategoryApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data as ExpenseCategoryApiRecord;
  }
  return null;
}

function mapApiRecord(record: ExpenseCategoryApiRecord): ExpenseCategoryRecord {
  const created = record.created_at || record.createdAt || new Date().toISOString();
  const updated = record.updated_at || record.updatedAt || created;
  return {
    id: String(record.id ?? ""),
    name: record.name,
    number: record.number ?? "",
    accountDetailTypeId: String(record.account_detail_type_id ?? ""),
    accountDetailTypeCode: record.account_detail_type_code ?? "",
    accountDetailTypeTitle: record.account_detail_type_title ?? "",
    createdAt: created,
    updatedAt: updated,
  };
}

export function listExpenseCategories(): ExpenseCategoryRecord[] {
  return load().sort((a, b) => a.name.localeCompare(b.name));
}

const PER_PAGE = 50;

export async function syncExpenseCategoriesFromApi() {
  try {
    const collected: ExpenseCategoryApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/expense-categories", {
        params: { page, per_page: PER_PAGE },
      });
      const body = response.data;
      collected.push(...extractRowsFromResponse(body));
      lastPage = readLastPageFromListBody(body) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listExpenseCategories();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    console.error("[ExpenseCategories] sync failed", error);
    return listExpenseCategories();
  }
}

export async function fetchExpenseDetailTypes(): Promise<ExpenseDetailTypeOption[]> {
  const response = await api.get<{ data?: Array<{ id: number | string; code: string; title: string }> }>(
    "/api/v1/expense-categories/detail-types",
  );

  return (response.data?.data ?? []).map((item) => ({
    id: String(item.id),
    code: item.code,
    title: item.title,
  }));
}

export async function fetchExpenseSubcategories(): Promise<ExpenseCategoryRecord[]> {
  const response = await api.get("/api/v1/expenses/subcategories");
  const records = dedupeById(
    extractRowsFromResponse(response.data)
      .map(mapApiRecord)
      .filter((r) => Boolean(r.id)),
  );
  save(records);
  return records.sort((a, b) => a.name.localeCompare(b.name));
}

export async function createExpenseCategory(draft: ExpenseCategoryDraft): Promise<UpsertExpenseCategoryResult> {
  const payload = {
    name: draft.name.trim(),
    account_detail_type_id: Number(draft.accountDetailTypeId),
  };
  const response = await api.post<UpsertExpenseCategoryApiResponse>("/api/v1/expense-categories", payload);
  const created = extractCategoryFromUpsertBody(response.data);
  if (!created || created.id == null) throw new Error("Invalid create expense category response.");
  const record = mapApiRecord(created);

  const items = load();
  const idx = items.findIndex((d) => d.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);

  return { record, message: response.data?.message || "Expense category created successfully." };
}

export async function updateExpenseCategory(id: ID, draft: ExpenseCategoryDraft): Promise<UpsertExpenseCategoryResult | null> {
  const items = load();
  const idx = items.findIndex((d) => d.id === id);
  if (idx === -1) return null;

  const payload = {
    name: draft.name.trim(),
    account_detail_type_id: Number(draft.accountDetailTypeId),
  };
  const response = await api.put<UpsertExpenseCategoryApiResponse>(`/api/v1/expense-categories/${id}`, payload);
  const updated = extractCategoryFromUpsertBody(response.data);
  if (!updated) throw new Error("Invalid update expense category response.");

  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);

  return { record, message: response.data?.message || "Expense category updated successfully." };
}

export async function deleteExpenseCategory(id: ID): Promise<DeleteExpenseCategoryResult> {
  const response = await api.delete<DeleteExpenseCategoryApiResponse>(`/api/v1/expense-categories/${id}`);
  const items = load();
  const next = items.filter((d) => d.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return { success: removed, message: response.data?.message || "Expense category deleted successfully." };
}

